import { kyraIcons } from '../ui/KyraIcon';
import type { IconLike } from '../ui/KyraIcon';

export type QuickActionModule = 'tesoreria' | 'membresias' | 'logistica';

export type QuickActionDefinition = {
  id: string;
  to: string;
  label: string;
  description: string;
  module: QuickActionModule;
  icon: IconLike;
  keywords: string[];
  requiredFeatures: string[];
  requiredScopesAny: string[];
};

export const quickActionDefinitions: QuickActionDefinition[] = [
  {
    id: 'treasury-deposit-create',
    to: '/operaciones/depositos?action=create',
    label: 'Registrar depósito',
    description: 'Registrar una entrada de dinero en una cuenta.',
    module: 'tesoreria',
    icon: kyraIcons.income,
    keywords: ['deposito', 'ingreso', 'abono', 'entrada', 'dinero', 'banco'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:write'],
  },
  {
    id: 'treasury-check-create',
    to: '/operaciones/cheques?action=create',
    label: 'Emitir cheque',
    description: 'Registrar un pago realizado mediante cheque.',
    module: 'tesoreria',
    icon: kyraIcons.cheque,
    keywords: ['cheque', 'pago', 'egreso', 'salida', 'emitir'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:write'],
  },
  {
    id: 'treasury-withdrawal-create',
    to: '/operaciones/retiros?action=create',
    label: 'Registrar retiro',
    description: 'Registrar una salida de efectivo de una cuenta.',
    module: 'tesoreria',
    icon: kyraIcons.expense,
    keywords: ['retiro', 'egreso', 'salida', 'efectivo'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:write'],
  },
  {
    id: 'treasury-own-transfer-create',
    to: '/operaciones/transferencias_propias?action=create',
    label: 'Transferir entre cuentas',
    description: 'Mover fondos entre cuentas de la organización.',
    module: 'tesoreria',
    icon: kyraIcons.swap,
    keywords: ['transferencia', 'traslado', 'cuentas', 'fondos', 'propia'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:write'],
  },
  {
    id: 'treasury-external-transfer-create',
    to: '/operaciones/transferencias_ajenas?action=create',
    label: 'Transferir a terceros',
    description: 'Registrar una transferencia hacia una cuenta externa.',
    module: 'tesoreria',
    icon: kyraIcons.ops,
    keywords: ['transferencia', 'tercero', 'externa', 'ajena', 'pago'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:write'],
  },
  {
    id: 'treasury-movements-query',
    to: '/operaciones?mode=consult',
    label: 'Consultar movimientos',
    description: 'Revisar las operaciones registradas en una cuenta.',
    module: 'tesoreria',
    icon: kyraIcons.analysis,
    keywords: ['consulta', 'movimientos', 'operaciones', 'historial', 'cuenta'],
    requiredFeatures: ['treasury'],
    requiredScopesAny: ['operations:read'],
  },
  {
    id: 'logistics-quotation-create',
    to: '/logistica/operaciones?type=COTIZACION&action=create',
    label: 'Nueva cotización',
    description: 'Preparar una propuesta comercial para un cliente.',
    module: 'logistica',
    icon: kyraIcons.doc,
    keywords: ['cotizacion', 'propuesta', 'precio', 'cliente'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-order-create',
    to: '/logistica/operaciones?type=PEDIDO&action=create',
    label: 'Crear pedido',
    description: 'Registrar una solicitud de productos o servicios.',
    module: 'logistica',
    icon: kyraIcons.order,
    keywords: ['pedido', 'orden', 'solicitud', 'cliente'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-dispatch-create',
    to: '/logistica/operaciones?type=DESPACHO&action=create',
    label: 'Programar despacho',
    description: 'Preparar una entrega y descontar existencias.',
    module: 'logistica',
    icon: kyraIcons.logistics,
    keywords: ['despacho', 'entrega', 'envio', 'salida', 'inventario'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-sale-create',
    to: '/logistica/operaciones?type=VENTA&action=create',
    label: 'Registrar venta',
    description: 'Crear una venta de productos o servicios.',
    module: 'logistica',
    icon: kyraIcons.cart,
    keywords: ['venta', 'vender', 'cliente', 'factura', 'cobro'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-purchase-create',
    to: '/logistica/operaciones?type=COMPRA&action=create',
    label: 'Registrar compra',
    description: 'Registrar una compra y su entrada de productos.',
    module: 'logistica',
    icon: kyraIcons.cart,
    keywords: ['compra', 'comprar', 'proveedor', 'pago', 'inventario'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-entry-create',
    to: '/logistica/operaciones?type=INGRESO&action=create',
    label: 'Registrar ingreso de inventario',
    description: 'Aumentar existencias sin registrar una compra.',
    module: 'logistica',
    icon: kyraIcons.stock,
    keywords: ['ingreso', 'entrada', 'inventario', 'existencia', 'stock'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'logistics-exit-create',
    to: '/logistica/operaciones?type=EGRESO&action=create',
    label: 'Registrar egreso de inventario',
    description: 'Registrar una salida o ajuste de existencias.',
    module: 'logistica',
    icon: kyraIcons.adjust,
    keywords: ['egreso', 'salida', 'inventario', 'existencia', 'ajuste'],
    requiredFeatures: ['logistics'],
    requiredScopesAny: ['logistics:write', 'logistics:operations:write'],
  },
  {
    id: 'memberships-enroll-create',
    to: '/membresias/miembros?action=create',
    label: 'Inscribir miembro',
    description: 'Crear una membresía para una persona.',
    module: 'membresias',
    icon: kyraIcons.useradd,
    keywords: ['miembro', 'inscripcion', 'inscribir', 'membresia', 'afiliacion', 'alta'],
    requiredFeatures: ['memberships'],
    requiredScopesAny: ['members:write'],
  },
  {
    id: 'memberships-receipt-create',
    to: '/membresias/recibos',
    label: 'Emitir recibo',
    description: 'Registrar el cobro de cuotas o cargos.',
    module: 'membresias',
    icon: kyraIcons.receipt,
    keywords: ['recibo', 'cobro', 'pago', 'cuota', 'caja'],
    requiredFeatures: ['memberships'],
    requiredScopesAny: ['members:write'],
  },
];
